import { api } from 'api';
import { RESET_FX_ADD_TRANSACTION_INFORMATION_STORE } from 'constants/fx';
import { RESET_FORM_ADD_PAYTO_ACCOUNT_STORE } from 'features/to/const';
import { checkMaintenance } from 'helpers/maintenance';
import { useNavigate } from 'react-router-dom';
import {
  CLEAR_DENOMINATIONS,
  CLEAR_NOTES,
  SET_FX_ADD_TRANSACTION_CURRENT_STEP,
  SET_FX_ADD_TRANSACTION_INFORMATION,
} from 'store/fx/action';
import {
  ACCESS_TOKEN,
  DASHBOARD_PREFIX,
  DEVICE_TOKEN,
  ORDER_EMAIL,
  PUSH_API_STATUS,
  RESET_ADD_TRANSACTION_INFORMATION_STORE,
  RESET_BACKDROP_STORE,
  RESET_EDIT_RECEIVER_INFORMATION_STORE,
  RESET_PORTAL_MODAL_STORE,
} from '../constants';
import Cookies from '../helpers/cookies';
import {
  SET_ACCOUNT_AGREEMENT_EDITING,
  SET_ADD_PAYTO_ACCOUNT_INFORMATION,
  SET_ADD_TRANSACTION_CURRENT_STEP,
  SET_ADD_TRANSACTION_INFORMATION,
  SET_ALL_TRANSACTIONS_AS_CANCELLED,
  SET_CURRENT_RECEIVER,
  SET_CURRENT_USER,
  SET_EDIT_RECEIVER_INFORMATION,
  SET_EVENTS,
  SET_EVENTS_ON_SIDEBAR,
  SET_EVENT_AVAILABLE,
  SET_LOADING_MESSAGE,
  SET_OPEN_REFRESH_TOKEN_POPUP,
  SET_PAYTO_ACCOUNT_AUTHORISING,
  SET_PAYTO_MAX_AMOUNT_EDITED,
  SET_PORTAL_MODAL,
  SET_REFERRAL_PARTICIPANTS,
  SET_SHOW_BACKDROP,
  SET_SHOW_LOADING,
  SET_SHOW_PENDING,
  SET_SHOW_SEARCHING,
  SET_TRANSACTIONS_COMPLETED_EMPTY,
  SET_TRANSACTIONS_EMPTY,
} from '../store/action';
import { MAKE_PAYMENT_KEY } from './form-add-transaction/useAddTransaction';
import { FX_MAKE_PAYMENT_KEY } from './fx/useFxAddTransaction';
import useFxStore from './fx/useFxStore';
import useCheckVersion from './useCheckVersion';
import { useDetectDevice } from './useDetectDevice';
import useFirebase, { requestNotifyPermission } from './useFirebase';
import usePortalModal from './usePortalModal';
import useStore from './useStore';

const useAuth = () => {
  const { state, dispatch } = useStore();
  const { dispatch: fxDispatch } = useFxStore();
  const { currentUser, portalModal } = state || {};
  const { firebaseAppDefault, saveDeviceToken } = useFirebase();
  const navigate = useNavigate();
  const { isDesktop } = useDetectDevice();
  const { fetchBuildVersion } = useCheckVersion();
  const { closePortalModalName } = usePortalModal();

  const token = Cookies.get(ACCESS_TOKEN);
  const isAuthenticated = token && currentUser;

  const login = async (email, password, urlRedirect) => {
    try {
      const { data } = await api.login(email, password);

      if (data?.token) {
        const { token } = data || {};

        Cookies.set(ACCESS_TOKEN, token);

        dispatch({ type: SET_OPEN_REFRESH_TOKEN_POPUP, payload: false });

        if (isDesktop && Notification?.permission === 'denied') {
          Cookies.set(PUSH_API_STATUS, 'denied');
        }
        requestNotifyPermission(
          firebaseAppDefault,
          process.env.REACT_APP_NOTIFICATION_VAPID_KEY,
          (deviceToken) => {
            saveDeviceToken({
              token,
              isActive: true,
              deviceToken,
            });
            Cookies.set(DEVICE_TOKEN, deviceToken);
          }
        );

        fetchBuildVersion();

        navigate(urlRedirect || `${DASHBOARD_PREFIX}`);
      }
      return null;
    } catch (error) {
      checkMaintenance(error);
      console.error(`🔥 API ❌ useAuth ➡️ login ↩`, error?.message);

      dispatch({ type: SET_OPEN_REFRESH_TOKEN_POPUP, payload: false });

      checkMaintenance(error);

      return error;
    }
  };

  const logout = async () => {
    saveDeviceToken({ token, isActive: false });
    api.logout(token);
    // sw.unregister(FIREBASE_MESSAGING_SW);
    Cookies.remove(PUSH_API_STATUS);

    Cookies.remove(ACCESS_TOKEN);

    dispatch({ type: SET_CURRENT_USER, payload: null });

    dispatch({
      type: SET_EDIT_RECEIVER_INFORMATION,
      payload: RESET_EDIT_RECEIVER_INFORMATION_STORE,
    });

    /* BEGIN - clean up send money flow */
    Cookies.remove(MAKE_PAYMENT_KEY);
    dispatch({
      type: SET_ADD_TRANSACTION_INFORMATION,
      payload: RESET_ADD_TRANSACTION_INFORMATION_STORE,
    });
    dispatch({ type: SET_ADD_TRANSACTION_CURRENT_STEP, payload: 0 });
    dispatch({
      type: SET_ADD_PAYTO_ACCOUNT_INFORMATION,
      payload: RESET_FORM_ADD_PAYTO_ACCOUNT_STORE,
    });
    /* END - clean up send money flow */

    /* BEGIN - clean up buy money flow */
    Cookies.remove(FX_MAKE_PAYMENT_KEY);
    Cookies.remove(ORDER_EMAIL);
    fxDispatch({
      type: SET_FX_ADD_TRANSACTION_INFORMATION,
      payload: RESET_FX_ADD_TRANSACTION_INFORMATION_STORE,
    });
    fxDispatch({
      type: SET_FX_ADD_TRANSACTION_CURRENT_STEP,
      payload: 0,
    });
    fxDispatch({
      type: CLEAR_DENOMINATIONS,
      payload: {},
    });
    fxDispatch({
      type: CLEAR_NOTES,
      payload: '',
    });
    /* END - clean up buy money flow */

    dispatch({ type: SET_CURRENT_RECEIVER, payload: null });

    dispatch({ type: SET_OPEN_REFRESH_TOKEN_POPUP, payload: false });

    dispatch({ type: SET_EVENT_AVAILABLE, payload: null });

    dispatch({ type: SET_EVENTS_ON_SIDEBAR, payload: null });
    dispatch({ type: SET_REFERRAL_PARTICIPANTS, payload: null });
    dispatch({ type: SET_TRANSACTIONS_EMPTY, payload: false });
    dispatch({ type: SET_TRANSACTIONS_COMPLETED_EMPTY, payload: false });
    dispatch({ type: SET_ALL_TRANSACTIONS_AS_CANCELLED, payload: false });

    dispatch({
      type: SET_ADD_PAYTO_ACCOUNT_INFORMATION,
      payload: RESET_FORM_ADD_PAYTO_ACCOUNT_STORE,
    });
    dispatch({
      type: SET_PAYTO_MAX_AMOUNT_EDITED,
      payload: null,
    });
    dispatch({
      type: SET_PAYTO_ACCOUNT_AUTHORISING,
      payload: null,
    });
    dispatch({
      type: SET_ACCOUNT_AGREEMENT_EDITING,
      payload: false,
    });

    dispatch({ type: SET_EVENTS, payload: null });

    dispatch({ type: SET_SHOW_BACKDROP, payload: RESET_BACKDROP_STORE });

    dispatch({ type: SET_PORTAL_MODAL, payload: RESET_PORTAL_MODAL_STORE });

    dispatch({ type: SET_LOADING_MESSAGE, payload: '' });

    dispatch({ type: SET_SHOW_PENDING, payload: false });

    dispatch({ type: SET_SHOW_SEARCHING, payload: false });

    fetchBuildVersion();

    closePortalModalName();

    dispatch({ type: SET_SHOW_LOADING, payload: false });
  };

  const refreshToken = async (tryCb, catchCb) => {
    try {
      const { data } = await api.refreshToken(token);

      if (data) {
        Cookies.set(ACCESS_TOKEN, data.token);
      }

      tryCb(data.token);

      fetchBuildVersion();
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);

      catchCb();
    }
  };

  return { token, isAuthenticated, login, logout, refreshToken };
};

export default useAuth;
